import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import { motion } from 'motion/react';
import { Logo } from './Logo';

export function Footer() {
  const currentYear = new Date().getFullYear();
  
  const socialLinks = [
    { icon: <Instagram className="w-5 h-5" />, href: "https://www.instagram.com/amasqis.ai?igsh=OWZ4Nnhlb3dweWI4", label: "Instagram" },
    { icon: <Facebook className="w-5 h-5" />, href: "https://www.facebook.com/profile.php?id=61572970796043&mibextid=qi2Omg&rdid=S5JiOw8a4yNLBh88#", label: "Facebook" },
    { icon: <Linkedin className="w-5 h-5" />, href: "https://www.linkedin.com/company/ai-amasqis/", label: "LinkedIn" },
    { icon: <Mail className="w-5 h-5" />, href: "mailto:info@amasqis.ai", label: "Email" },
  ];

  const quickLinks = [
    { name: "About Us", href: "/about" },
    { name: "Solutions", href: "/solutions" },
    { name: "Case Studies", href: "/case-studies" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ];

  const solutions = [
    { name: "Operational Intelligence & Automation", href: "/solutions/operational-intelligence-automation" },
    { name: "AI-Powered Decision Making", href: "/solutions/ai-powered-decision-making" },
    { name: "Workplace Safety & Compliance", href: "/solutions/workplace-safety-compliance" },
    { name: "Digital Transformation & IT Enablement", href: "/solutions/digital-transformation-it-enablement" },
  ];

  // Handle social link clicks with proper event handling
  const handleSocialClick = (href: string, label: string) => {
    // Add analytics or tracking if needed
    console.log(`Clicked ${label}: ${href}`);
    // Let the default browser behavior handle the link
  };

  return (
    <footer className="w-full bg-[#050505] border-t border-white/10 relative z-20">
      <div className="w-full max-w-none mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* First Row - 3 Columns: Logo/Info/Socials, Quick Links, Solutions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-8 lg:mb-12">
          {/* Company Info with Logo and Socials */}
          <div className="space-y-4 lg:space-y-6">
            <Logo variant="footer" size="md" />
             
            <p className="amasqis-text text-white leading-relaxed text-left">
            Agents and Multi-Agent Systems Quantum Intelligent Software in AI
            </p>

            <p className="text-muted-foreground leading-relaxed text-left">
              Transforming industries with cutting-edge AI solutions. 
              From predictive maintenance to bioinformatics, we deliver innovation that matters.
            </p>

            <div className="flex flex-wrap gap-3 sm:gap-4">
              {socialLinks.map((link) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white/5 hover:bg-primary/20 transition-colors group relative z-30"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSocialClick(link.href, link.label)}
                  style={{ pointerEvents: 'auto' }}
                >
                  <div className="text-muted-foreground group-hover:text-primary transition-colors">
                    {link.icon}
                  </div>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="text-left md:text-center">
            <h4 className="font-bold mb-4 text-left md:text-center">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name} className="text-left md:text-center">
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors relative z-30 block text-left md:text-center"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log(`Navigating to: ${link.href}`);
                    }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-bold mb-4 text-left">Solutions</h4>
            <ul className="space-y-2">
              {solutions.map((solution) => (
                <li key={solution.name}>
                  <Link
                    to={solution.href}
                    className="text-muted-foreground hover:text-primary transition-colors relative z-30 block text-left"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log(`Navigating to: ${solution.href}`);
                    }}
                  >
                    {solution.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Second Row - 4 Columns: Country Contact Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-8 lg:mb-12">
          {/* amasQIS OMAN */}
          <div>
            <h5 className="text-sm font-semibold text-primary mb-3 amasqis-text">amasQIS.ai OMAN</h5>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground text-sm">
                  <a 
                    href="https://maps.google.com/?q=Muscat+Governorate+Al+Mawaleh+Oman&ll=23.5880,58.3829&z=12"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors cursor-pointer relative z-30"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Opening Oman location in maps');
                    }}
                  >
                    <p>Muscat Governorate - Al Mawaleh</p>
                    <p>P.O. Box: 2223, Postal Code: 123</p>
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <a 
                  href="tel:+96879262675"
                  className="text-muted-foreground text-sm hover:text-primary transition-colors relative z-30"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Calling Oman office');
                  }}
                >
                  +968 7926 2675
                </a>
              </div>
            </div>
          </div>

          {/* amasQIS INDIA */}
          <div>
            <h5 className="text-sm font-semibold text-primary mb-3 amasqis-text">amasQIS.ai INDIA</h5>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground text-sm">
                  <a 
                    href="https://maps.app.goo.gl/J9zZqVEGEeHht3rY7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors cursor-pointer relative z-30"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Opening India location in maps');
                    }}
                  >
                    <p>Sasurie College Campus, Coimbatore Bypass (NH544) Vijayamangalam, Tirupur Dt, Tamil Nadu – 638056.</p>
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <a 
                  href="tel:+919025226518"
                  className="text-muted-foreground text-sm hover:text-primary transition-colors relative z-30"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Calling India office');
                  }}
                >
                  +91 90252 26518
                </a>
              </div>
            </div>
          </div>

          {/* amasQIS DUBAI */}
          <div className="text-left md:text-center">
            <h5 className="text-sm font-semibold text-primary mb-3 amasqis-text text-left md:text-center">amasQIS.ai DUBAI</h5>
            <div className="space-y-3">
              <div className="flex items-start space-x-2 justify-start md:justify-center">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground text-sm text-left md:text-center">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=AL%20SHARIF%20BUILDING%202%2C2.%20post%20Box%20%3A88878%2C%20Dubai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors cursor-pointer relative z-30"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Opening Dubai location in maps');
                    }}
                  >
                    <p>AL Sharif Building 2,2.</p>
                    <p>post Box :88878, Dubai</p>
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-2 justify-start md:justify-center">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <a 
                  href="tel:+917708113987"
                  className="text-muted-foreground text-sm hover:text-primary transition-colors relative z-30 text-left md:text-center"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Calling Oman office');
                  }}
                >
                  +91 77081 13987
                </a>
              </div>
            </div>
          </div>

          {/* amasQIS UK */}
          <div>
            <h5 className="text-sm font-semibold text-primary mb-3 amasqis-text">amasQIS.ai UK</h5>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground text-sm">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=128%20CITY%20ROAD%2C%20LONDON%2C%20EC1V%202NX%2C%20UNITED%20KINGDOM"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors cursor-pointer relative z-30"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Opening UK location in maps');
                    }}
                  >
                    <p>128 City Road, London</p>
                    <p>United Kingdom</p>
                    <p>Postcode: EC1V 2NX</p>
                  </a>
                </div>
              </div>
              {/* <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <a
                  href="mailto:enquiry.uk@amasqis.ai"
                  className="text-muted-foreground text-sm hover:text-primary transition-colors relative z-30"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Emailing UK office');
                  }}
                >
                  enquiry.uk@amasqis.ai
                </a>
              </div> */}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            <p className="text-muted-foreground text-sm text-left">
              <span className="amasqis-text">Agents and Multi-Agent Systems Quantum Intelligent Software in AI</span>
            </p>
            <p className="text-muted-foreground text-sm text-left md:text-right">
              © {currentYear} <span className="amasqis-text">amasQIS.ai</span> - All rights reserved.
            </p>
            
            {/* <div className="flex flex-wrap gap-4 lg:gap-6 text-sm justify-start md:justify-end">
              <Link 
                to="/contact" 
                className="text-muted-foreground hover:text-primary transition-colors relative z-30"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Navigating to privacy policy');
                }}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/contact" 
                className="text-muted-foreground hover:text-primary transition-colors relative z-30"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Navigating to terms of service');
                }}
              >
                Terms of Service
              </Link>
              <Link 
                to="/contact" 
                className="text-muted-foreground hover:text-primary transition-colors relative z-30"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Navigating to cookie policy');
                }}
              >
                Cookie Policy
              </Link>
            </div> */}
          </div>
        </div>
      </div>

      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
    </footer>
  );
}